import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes } from 'preact';
import { cn } from '../../lib/utils';
import { Slot } from './slot';

/**
 * Bouton — anatomie shadcn/ui (style « New York »), jetons SYGAP.
 *
 * Deux écarts assumés par rapport à l'amont :
 *
 * 1. La taille `tactile` (48 px) n'existe pas chez shadcn. Elle est imposée par
 *    le système de design sur le portail et la PWA : doigts abîmés, écran rayé,
 *    usage en mouvement. Les 36 px de `default` conviennent au poste agent, où
 *    l'on travaille à la souris toute la journée, pas au patron de pirogue.
 * 2. Le bouton s'enfonce légèrement au clic (`active:translate-y-px`). C'est le
 *    seul mouvement gratuit du système, et il est assumé : sur un écran tactile
 *    bas de gamme où le retour visuel du navigateur est lent, il confirme que
 *    l'appui a bien été pris — un agent qui doute appuie deux fois.
 */
const varianteBouton = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium outline-none transition-[background-color,color,transform] duration-100 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-card hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        // Un lien reste souligné : jamais signalé par la seule couleur.
        link: 'text-primary underline underline-offset-4',
      },
      size: {
        default: 'h-10 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-9 gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-11 px-6 has-[>svg]:px-4',
        tactile: 'h-12 px-6 text-base',
        icon: 'size-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ProprietesButton
  extends Omit<ButtonHTMLAttributes, 'size'>, VariantProps<typeof varianteBouton> {
  /** Délègue le rendu à l'enfant — un lien stylé en bouton, plutôt qu'imbriqué. */
  readonly asChild?: boolean;
}

export function Button({ className, variant, size, asChild = false, ...reste }: ProprietesButton) {
  const Composant = asChild ? Slot : 'button';
  return (
    <Composant
      data-slot="button"
      className={cn(varianteBouton({ variant, size }), className)}
      {...reste}
    />
  );
}

export { varianteBouton };
